import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CreditCard,
  Check,
  Zap
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPage = () => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch pricing
    axios.get(`${API}/pricing`).then(res => setPricing(res.data));
    
    // Check for success/cancel from Stripe
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error("Payment status check timed out");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Your license has been created.");
        navigate("/admin/licenses");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Status check error:", error);
    }
  };

  const handleCheckout = async (plan) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        plan,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  const plans = [
    {
      id: "starter",
      name: "Starter",
      description: "For individuals and small projects",
      features: ["10,000 logs/month", "Basic analytics", "CSV export", "Email support"]
    },
    {
      id: "pro",
      name: "Pro",
      description: "For growing businesses",
      popular: true,
      features: ["100,000 logs/month", "Email analyzer", "Link tracker", "VPN detection", "API access", "Phone lookup"]
    },
    {
      id: "business",
      name: "Business",
      description: "For teams and agencies",
      features: ["Unlimited logs", "All Pro features", "White-label", "Webhooks", "Priority support", "Email tracking"]
    },
    {
      id: "enterprise",
      name: "Enterprise",
      description: "For large organizations",
      features: ["Everything in Business", "Self-hosted option", "SLA guarantee", "Custom integrations", "Dedicated support"]
    }
  ];

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl md:text-4xl font-mono font-bold tracking-tight">
          Simple, Transparent Pricing
        </h1>
        <p className="text-muted-foreground mt-2">
          Choose the plan that fits your needs
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center">
        <div className="bg-card border border-border rounded-lg p-1 flex gap-1">
          <Button
            variant={billingCycle === "monthly" ? "default" : "ghost"}
            size="sm"
            onClick={() => setBillingCycle("monthly")}
          >
            Monthly
          </Button>
          <Button
            variant={billingCycle === "annual" ? "default" : "ghost"}
            size="sm"
            onClick={() => setBillingCycle("annual")}
            className="gap-2"
          >
            Annual
            <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">Save 17%</Badge>
          </Button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => (
          <Card 
            key={plan.id}
            className={`bg-card/50 border-border relative ${plan.popular ? "border-primary ring-1 ring-primary" : ""}`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
              </div>
            )}
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-lg">{plan.name}</CardTitle>
              <p className="text-xs text-muted-foreground">{plan.description}</p>
            </CardHeader>
            <CardContent className="text-center">
              {pricing && (
                <div className="mb-4">
                  <span className="text-3xl font-mono font-bold">
                    ${pricing[plan.id]?.[billingCycle] || 0}
                  </span>
                  <span className="text-muted-foreground">
                    /{billingCycle === "annual" ? "year" : "month"}
                  </span>
                </div>
              )}
              
              <ul className="text-sm text-left space-y-2 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <Button 
                className="w-full gap-2" 
                variant={plan.popular ? "default" : "outline"}
                onClick={() => handleCheckout(plan.id)}
                disabled={loading}
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4" />
                )}
                Get Started
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Features */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-5 h-5 text-primary" />
            <h3 className="font-medium">All plans include</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-muted-foreground">
            <div>• Real-time IP logging</div>
            <div>• Geolocation data</div>
            <div>• Device detection</div>
            <div>• Threat alerts</div>
            <div>• Blacklist/Whitelist</div>
            <div>• Data export</div>
            <div>• 30-day retention (min)</div>
            <div>• SSL encryption</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PricingPage;
